import { type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { PlaceWithDistance } from '@shared/types';
import { LOCALITY_LABELS } from '@shared/locality';
import { formatDriveTime, formatDistance } from '@/lib/formatting';
import { telUrl } from '@shared/maps';
import { HeartIcon } from './Icons';
import { InterestCheck } from './InterestCheck';
import { PlaceAvatar } from './PlaceAvatar';
import { PlaceRowNav } from './PlaceRowNav';
import { useInterests } from '@/hooks/useInterests';
import { CATEGORY_SHORT, INTEREST_CATALOG } from '@/lib/interests';
import { formatClockInText } from '@shared/clock';
import { appendEnglish, glossInline } from '@shared/ptEn';
import { Bilingual } from './Bilingual';
import { PlaceHours } from './OpeningHours';

export function PlaceCard({
  place,
  isFavorite,
  onToggleFavorite,
  now,
}: {
  place: PlaceWithDistance;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  now?: Date;
}) {
  const navigate = useNavigate();
  const { hasInterest, toggleInterest } = useInterests();
  const interest = INTEREST_CATALOG.find((i) => i.placeCategory === place.category);

  return (
    <article
      className="place-card place-card--tappable"
      onClick={(e: MouseEvent<HTMLElement>) => {
        if ((e.target as HTMLElement).closest('a, button')) return;
        navigate(`/places/${place.slug}`);
      }}
    >
      {place.imageUrl ? (
        <img className="place-card-image" src={place.imageUrl} alt="" />
      ) : null}
      <div className="place-card-header">
        <div className="place-card-header__identity">
          <PlaceAvatar name={place.name} category={place.category} imageUrl={place.imageUrl} size={48} />
          <div className="place-card-header__copy">
            <h3 className="place-card-name">
              <Link to={`/places/${place.slug}`}>
                <Bilingual text={place.name} />
              </Link>
            </h3>
            <p className="place-card-meta">
              {formatDistance(place.distance_km)} · {formatDriveTime(place.estimated_drive_minutes)} away
            </p>
          </div>
        </div>
        <div className="place-card-header__tools">
          {interest ? (
            <InterestCheck
              checked={hasInterest(interest.id)}
              label={interest.label}
              onToggle={() => toggleInterest(interest.id)}
            />
          ) : null}
          {onToggleFavorite ? (
            <button
              type="button"
              className={`icon-btn ${isFavorite ? 'is-on' : ''}`}
              aria-pressed={isFavorite}
              aria-label={appendEnglish(
                isFavorite ? `Remove ${place.name} from favorites` : `Save ${place.name} to favorites`,
                place.name,
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorite(place.id);
              }}
            >
              <HeartIcon filled={Boolean(isFavorite)} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="place-card__badges">
        <span className="place-badge">{CATEGORY_SHORT[place.category]}</span>
        <span className="place-badge place-badge--ghost">{LOCALITY_LABELS[place.locality]}</span>
        <PlaceRowNav place={place} />
      </div>
      <PlaceHours openingHours={place.openingHours} now={now} />

      {place.notes ? <p className="place-card__notes">{glossInline(formatClockInText(place.notes))}</p> : null}
      <p className="place-card__addr">
        <Bilingual text={place.address} />
      </p>

      {place.phone ? (
        <div className="action-row">
          <a className="action-btn" href={telUrl(place.phone)} onClick={(e) => e.stopPropagation()} aria-label={appendEnglish(`Call ${place.name}`, place.name)}>
            Call
          </a>
        </div>
      ) : null}
    </article>
  );
}
